import find from 'lodash/find';
import moment from 'moment';
import BaseSubscription from './BaseSubscription';
import { store } from './../store';
import services, { logInfo } from './services';
import { fragmentCalendar, serializeWorkHours } from './serializers';
import { getFromLocalStorage, setToLocalStorage, stringifyTimePeriod } from './../utils/utilsGlobal';
import {
  setWorkHoursSchedulerReasons,
  setWorkHoursSchedulerIsWorking,
  setWorkHoursSchedulerCanSwitchOff,
} from './../actions/actions';
import {
  gql,
  sendAPIRequest,
  insertGraphQLFragments,
} from './GraphQLClient';


export const WORK_HOURS_SCHEDULER_TITLE_UPDATE = 'WORK_HOURS_SCHEDULER_TITLE_UPDATE';
export const WORK_HOURS_SCHEDULER_IS_WORKING_UPDATE = 'WORK_HOURS_SCHEDULER_IS_WORKING_UPDATE';
export const WORK_HOURS_SCHEDULER_CAN_SWITCH_OFF_UPDATE = 'WORK_HOURS_SCHEDULER_CAN_SWITCH_OFF_UPDATE';


export default class WorkHoursScheduler extends BaseSubscription {

  TITLE_UPDATE_INTERVAL = 30 * 1000;
  IS_WORKING_REQUEST_INTERVAL = 30 * 1000;

  workHours = [];
  currentCompany = {};
  currentOperator = {};

  isWorkingRequestInterval = null;
  titleUpdateInterval = null;

  title = {
    text: '',
    textShort: '',
    isHighlighted: false,
    upcomingEvent: {},
  };
  isWorking = true;
  canSwitchOff = true;

  workHoursQuery = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $startDate: Date!, $endDate: Date!) {
      workCalendar(companyId: $companyId, startDate: $startDate, endDate: $endDate) {
        ...fragmentCalendar
      }
    }
  `, { fragmentCalendar });

  timeOffReasonsQuery = gql`
    query ($companyId: Uuid!) {
      switchChangeReasonTypes(companyId: $companyId) {
        id
        title
      }
    }
  `;

  switchManuallyMutation = gql`
    mutation ($companyId: Uuid!, $isWorking: Boolean!, $reasonId: ID, $comment: String) {
      changeSwitch(companyId: $companyId, status: $isWorking, reasonTypeId: $reasonId, reasonText: $comment) {
        id
        isWorking
      }
    }
  `;

  isWorkingSubscription = gql`
    subscription ($companyId: Uuid!) {
      changeSwitch(companyId: $companyId) {
        status
        changedBy
        reasonType { id, title }
      }
    }
  `;

  switchOffAvailabilitySubscription = gql`
    subscription ($companyId: Uuid!){
      switchOffAvailability(companyId: $companyId) {
        available
      }
    }
  `;

  isWorkingQuery = gql`
    query ($companyId: Uuid!) {
      profile(companyId: $companyId) {
        isWorking
      }
    }
  `;

  initialize = (params) => {
    const { currentOperator, currentCompany } = params || {};
    this.currentCompany = currentCompany;
    this.currentOperator = currentOperator;

    this.subscribeToIsWorkingChange();
    this.subscribeToSwitchOffAvailabilityChange();
    this.checkIfIsWorkingChanged(this.currentOperator.isWorking);
    this.checkIfCanSwitchOffChanged(this.currentOperator.switchOffAvailable);

    this.fetchWorkHours().then(workHours => {
      this.workHours = workHours;
      this.checkIfTitleChanged();
      logInfo('WorkHoursScheduler: Subscribed', { workHours, currentOperator });

      this.titleUpdateInterval = setInterval(this.checkIfTitleChanged, this.TITLE_UPDATE_INTERVAL);
      this.isWorkingRequestInterval = setInterval(() => {
        this.fetchIsWorking().then(this.checkIfIsWorkingChanged);
      }, this.IS_WORKING_REQUEST_INTERVAL);

      this.fetchTimeOffReasons().then(timeOffReasons => {
        store.dispatch(setWorkHoursSchedulerReasons(timeOffReasons));
      });
    });
  };

  checkIfTitleChanged = () => {
    const title = this.getCurrentTitle();
    if (title.text !== this.title.text) {
      this.title = title;
      this.triggerEvent(WORK_HOURS_SCHEDULER_TITLE_UPDATE, title);
    }
  };

  fetchWorkHours = () => {
    const startDate = new Date().toISOString().split('T')[0];
    const endDate = moment(startDate).add(31, 'days').format().split('T')[0];
    const variables = {
      companyId: this.currentCompany.id,
      startDate,
      endDate,
    };
    return sendAPIRequest(this.workHoursQuery, variables).then(serializeWorkHours);
  };

  fetchTimeOffReasons = () => {
    return sendAPIRequest(this.timeOffReasonsQuery, {
      companyId: this.currentCompany.id,
    });
  };

  isWithinPeriod = (datetime, period) => {
    const { from, to } = period;
    if (!from || !to) {
      return false;
    }
    else {
      return datetime >= from && datetime < to;
    }
  };

  getUpcomingEventByTimestamp = (datetime) => {
    let upcomingEventType = null;
    let willHappenAt = null;
    let titleMask = '';
    let titleMaskShort = '';
    let isSupposedToBeWorkingNow = null;

    const ongoingWorkDay = find(this.workHours, day => this.isWithinPeriod(datetime, day));
    const ongoingBreak  = find(ongoingWorkDay?.breaks, breakItem => this.isWithinPeriod(datetime, breakItem));
    const followingWorkDay = find(this.workHours, day => day.from > datetime);

    if (ongoingWorkDay) {
      if (ongoingBreak) {
        upcomingEventType = 'WAITING_BREAK_TO_END';
        titleMask = 'До конца перерыва %s';
        willHappenAt = ongoingBreak.to;
        isSupposedToBeWorkingNow = false;
      }
      else {
        const followingBreak = find(ongoingWorkDay?.breaks, breakItem => breakItem.from > datetime);
        if (followingBreak) {
          upcomingEventType = 'WAITING_BREAK_TO_START';
          titleMask = 'До перерыва %s';
          willHappenAt = followingBreak.from;
          isSupposedToBeWorkingNow = true;
        }
        else {
          upcomingEventType = 'WAITING_WORKDAY_TO_END';
          titleMask = 'До конца рабочего дня %s';
          titleMaskShort = 'До конца работы %s';
          willHappenAt = ongoingWorkDay.to;
          isSupposedToBeWorkingNow = true;
        }
      }
    }
    else if (followingWorkDay) {
      const followingDayStartsInThreeHours = followingWorkDay.from - Date.now() < 3 * 60 * 60 * 1000;
      if (followingDayStartsInThreeHours) {
        upcomingEventType = 'WAITING_WORKDAY_TO_START_IN_3_HOURS';
        titleMask = 'До начала рабочего дня %s';
        titleMaskShort = 'До начала работы %s';
        willHappenAt = followingWorkDay.from;
        isSupposedToBeWorkingNow = false;
      }
      else {
        upcomingEventType = 'WAITING_WORKDAY_TO_START_IN_MORE_THAN_3_HOURS';
        titleMask = 'Рабочий день окончен. Отдыхайте!';
        titleMaskShort = 'Рабочий день окончен!';
        willHappenAt = followingWorkDay.from;
        isSupposedToBeWorkingNow = false;
      }
    }
    return {
      upcomingEventType,
      willHappenAt,
      titleMask,
      titleMaskShort,
      isSupposedToBeWorkingNow,
    };
  };

  detectTakingUnscheduledBreak = (isWorking) => {
    const { isSupposedToBeWorkingNow } = this.getUpcomingEventByTimestamp(Date.now());
    return isSupposedToBeWorkingNow && !isWorking;
  };

  getCurrentTitle = () => {
    const now = new Date();
    const upcomingEvent = this.getUpcomingEventByTimestamp(now);
    const { willHappenAt, titleMask, titleMaskShort } = upcomingEvent;

    if (this.currentOperator.isManager) {
      return {
        text: '',
        textShort: '',
        isHighlighted: false,
        upcomingEvent,
      };
    }
    else if (this.detectTakingUnscheduledBreak(this.isWorking)) {
      let latestUnscheduledBreakStartedAt = getFromLocalStorage('LATEST_UNSCHEDULED_BREAK_STARTED_AT');
      if (!latestUnscheduledBreakStartedAt) {
        latestUnscheduledBreakStartedAt = Date.now();
        setToLocalStorage('LATEST_UNSCHEDULED_BREAK_STARTED_AT', latestUnscheduledBreakStartedAt);
      }
      const text = 'Перерыв ' + stringifyTimePeriod(now - latestUnscheduledBreakStartedAt, { format: 'string' });
      return {
        text,
        textShort: text,
        isHighlighted: false,
        upcomingEvent,
      };
    }
    else {
      const millisecondsUntilUpcomingEvent = willHappenAt - now;
      const minutesUntilUpcomingEvent = Math.ceil(millisecondsUntilUpcomingEvent / 1000 / 60);
      const formattedTimeUntilUpcomingEvent = stringifyTimePeriod(millisecondsUntilUpcomingEvent, { format: 'string' });
      const formattedTimeUntilUpcomingEventShort = stringifyTimePeriod(millisecondsUntilUpcomingEvent, {
        format: 'string',
        isCompact: true,
      });
      const text = titleMask.replace(/%s/g, formattedTimeUntilUpcomingEvent);
      const textShort = (titleMaskShort || titleMask).replace(/%s/g, formattedTimeUntilUpcomingEventShort);
      const isHighlighted = willHappenAt && minutesUntilUpcomingEvent <= 10;
      return {
        text,
        textShort,
        isHighlighted,
        upcomingEvent,
      };
    }
  };

  subscribeToIsWorkingChange = () => {
    services.graphQLClientSocket.subscribe({
      query: this.isWorkingSubscription,
      variables: { companyId: this.currentCompany.id },
      onResult: (response) => {
        const data = response.data?.changeSwitch || {};
        const isWorking = data.status;
        const hasManuallyDisabledSwitcher = !isWorking && data.changedBy === 'MANUAL';
        if (hasManuallyDisabledSwitcher) {
          setToLocalStorage('LATEST_UNSCHEDULED_BREAK_STARTED_AT', Date.now());
        }
        this.checkIfIsWorkingChanged(isWorking);
        this.checkIfTitleChanged();
      },
    });
  };

  subscribeToSwitchOffAvailabilityChange = () => {
    if (this.currentOperator.hasSchedule) {
      services.graphQLClientSocket.subscribe({
        query: this.switchOffAvailabilitySubscription,
        variables: { companyId: this.currentCompany.id },
        onResult: (response) => {
          const data = response.data?.switchOffAvailability || {};
          const canSwitchOff = data.available;
          this.checkIfCanSwitchOffChanged(canSwitchOff);
        },
      });
    }
  };

  switchManually = ({ isWorking, reasonId, comment }) => {
    const variables = {
      companyId: this.currentCompany.id,
      isWorking,
      reasonId,
      comment,
    };
    return sendAPIRequest(this.switchManuallyMutation, variables).then(data => {
      const isWorking = data?.isWorking;
      this.checkIfIsWorkingChanged(isWorking);
      this.checkIfTitleChanged();
    });
  };

  fetchIsWorking = () => {
    const variables = {
      companyId: this.currentCompany.id,
    };
    return sendAPIRequest(this.isWorkingQuery, variables)
      .then(data => Boolean(data?.isWorking))
      .catch(e => {
        e.overrideErrorNotification({ alert: false });
        throw e;
      });
  };

  checkIfIsWorkingChanged = (isWorking) => {
    if (isWorking !== this.isWorking) {
      this.isWorking = isWorking;
      this.triggerEvent(WORK_HOURS_SCHEDULER_IS_WORKING_UPDATE, isWorking);
      store.dispatch(setWorkHoursSchedulerIsWorking(isWorking));
    }
  };

  checkIfCanSwitchOffChanged = (canSwitchOff) => {
    if (canSwitchOff !== this.canSwitchOff) {
      this.canSwitchOff = canSwitchOff;
      this.triggerEvent(WORK_HOURS_SCHEDULER_CAN_SWITCH_OFF_UPDATE, canSwitchOff);
      store.dispatch(setWorkHoursSchedulerCanSwitchOff(canSwitchOff));
    }
  };

  unsubscribe = () => {
    logInfo('WorkHoursScheduler: Unsubscribing...');
    clearInterval(this.titleUpdateInterval);
    clearInterval(this.isWorkingRequestInterval);
    services.graphQLClientSocket.unsubscribe(this.isWorkingSubscription);
  };
}
